import { useEffect } from "react"

/**
 * Sync the app's appearance with the host CRM: dark/light mode and the tenant
 * brand colour. The host (app-shell) postMessages `{ type: "koortex:theme",
 * colorScheme, primaryColor }` on load and whenever either changes; we toggle
 * the `.dark` class and repaint the `--accent`/`--primary` tokens (which the
 * selected tabs and primary buttons read) to the tenant colour.
 */
interface ThemeMessage {
  type: "koortex:theme"
  colorScheme?: "dark" | "light"
  primaryColor?: string | null
}

const HEX6 = /^#?[0-9a-fA-F]{6}$/

/** WCAG relative luminance (0=black, 1=white) of a #rrggbb colour. */
function luminance(hex: string): number {
  const int = parseInt(hex.replace("#", ""), 16)
  const chan = [(int >> 16) & 255, (int >> 8) & 255, int & 255].map((c) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * chan[0] + 0.7152 * chan[1] + 0.0722 * chan[2]
}

function applyTheme(msg: ThemeMessage): void {
  const root = document.documentElement
  if (msg.colorScheme) {
    root.classList.toggle("dark", msg.colorScheme === "dark")
  }
  const raw = (msg.primaryColor ?? "").trim()
  if (HEX6.test(raw)) {
    const hex = raw.startsWith("#") ? raw : `#${raw}`
    // Readable text on the brand colour: dark ink on light brands, white on dark.
    const fg = luminance(hex) > 0.55 ? "#0f172a" : "#ffffff"
    root.style.setProperty("--accent", hex)
    root.style.setProperty("--accent-foreground", fg)
    root.style.setProperty("--primary", hex)
    root.style.setProperty("--primary-foreground", fg)
  }
}

export function useHostTheme(hostOrigin?: string): void {
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      // Only trust theme messages from the host origin (from the token).
      if (hostOrigin && e.origin !== hostOrigin) return
      const data = e.data as ThemeMessage | undefined
      if (data && data.type === "koortex:theme") applyTheme(data)
    }
    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [hostOrigin])
}
